// @erp/ui/header — the shell's top bar, ported to match
// ../shippra/apps/libms/src/components/layout/PageHeader.tsx EXACTLY: the same
// Header CONTROLS are a 32px box with a 20px glyph — Adobe's own numbers,
// measured on their Help and Notifications buttons (2026-10-02), where ours
// had been running 13-16px glyphs in the same box and reading noticeably
// lighter beside theirs. The breadcrumb's own glyphs stay smaller: they are
// punctuation inside a line of 14px text, not controls.
//
// h-12 px-6 row. The crumb is `text-sm` (14px) with a 14px Home and a 12px
// chevron since 2026-10-02: every navigation row in the sidebar moved to 14px
// on Adobe's own measured values, and the breadcrumb was the last navigation
// text left at 12px — the one trail through the app set smaller than the list
// it leads out of. The 48px row is unchanged and still fits a 21px line.
// (historical: Home 11px + ChevronRight 10px + 12px muted crumb)
// trail on the left, and the same 7×7 rounded-300 icon buttons on the right.
//
// Departures, each for a requirement LibMS does not have:
//   • a hamburger (<lg) — LibMS is desktop-only; mini's drawer needs a trigger;
//   • help + theme toggles — mini ships contextual help and a dark theme;
//   • NO notification bell. The source's bell renders a hardcoded "3" over a
//     button that does nothing; mini has no notifications feature, and a badge
//     that lies is worse than an absent one. It lands when the feature does.
//   • the account avatar lives in the SIDEBAR's account row (one home), which
//     is where LibMS also shows name + email.
import { Show, type JSX } from "solid-js";
import { LayoutGrid, Menu as MenuIcon, Search } from "lucide-solid";
import { Link } from "@tanstack/solid-router";
import { BrandMark } from "../brand/index.js";
import { HelpToggle } from "../help-mode/index.js";
import { ThemeToggle } from "../theme-toggle/index.js";
import { openCommandPalette } from "../command-palette/index.js";

/** Kept exported: portals type their session→shell mapping against it, and the
 * sidebar's account row consumes the same shape. */
export interface HeaderUser {
  name: string;
  email?: string;
}

export function Header(props: {
  drawerId: string;
  /** Present ⇒ signed in; gates the jump-to-page trigger (nav-only search). */
  user: HeaderUser | null;
  /** Extra controls before the display toggles — e.g. the public OPAC's Sign in button. */
  actions?: JSX.Element;
  /** The portal's theme pair for the toggle (defaults to the product themes). */
  themes?: { light: string; dark: string };
  /**
   * Show/hide the module strip above this header. Omit both and no control
   * renders — a portal with one module has no band to toggle.
   */
  modulesOpen?: boolean;
  /** Flip `modulesOpen`. Present ⇒ the grid glyph renders at the header's start. */
  onToggleModules?: () => void;
  /** The wordmark beside the brand mark. Omit to render no brand block. */
  appName?: string;
  /** Brand-mark colour — a fixed asset colour, never a theme token. */
  brandColor?: string;
}) {
  return (
    // 56px to match the band above it and Adobe's own header, and a 16px inline
    // padding so the wordmark lands at x=16 where theirs is at 15 — px-6 had it
    // at 24, visibly further in than the rail beneath.
    <header class="flex h-14 shrink-0 items-center gap-4 px-4">
      {/* A real <button> (UX-13, 2026-09-18) — it used to be a `<label for>`,
          which is not a keyboard stop, so the drawer could not be opened
          without a pointer. Clicking the hidden checkbox flips daisyUI's
          drawer state and fires its change event, the same path the label
          took. The daisyUI `drawer-button` part class is deliberately omitted
          — its root `.drawer` lives in AppShell, and a part class without its
          root is meaningless here. */}
      {/* Icon-button state ink here and on the ⌘K button below: the --ui-state-*
          ramp (tokens.css) — key-focus == hover, down one step darker (Spectrum).
          Icon-button BOX here and below: size-(--ds-target), the component-height
          -100 hit floor (32px, 40 at touch) — the old h-7 w-7 literal froze the
          target at 28px on every device. Fallback = that old box, for a portal
          without spectrum.css. */}
      <button
        type="button"
        aria-label="Open navigation"
        class="-ml-1 flex size-[var(--ds-target,1.75rem)] shrink-0 items-center justify-center rounded-300 text-base-subtle transition-colors hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down) hover:text-base-content lg:hidden"
        onClick={() => document.getElementById(props.drawerId)?.click()}
      >
        <MenuIcon size={20} aria-hidden="true" />
      </button>

      {/* THE BAND TOGGLE, left of everything — Adobe's own position for it
          (2026-10-02: the grid glyph left of their wordmark hides the app strip
          above their header, and the page takes the 56px back). Ours does the
          same for the module strip: a librarian who lives in one module all day
          can put those 48px into the list they are reading, and get them back
          with the same glyph. The choice is remembered per browser. */}
      <Show when={props.onToggleModules}>
        <button
          type="button"
          id="header-toggle-modules"
          aria-label={props.modulesOpen ? "Hide modules" : "Show modules"}
          aria-expanded={props.modulesOpen}
          aria-controls="module-strip"
          class="-ml-1 flex size-[var(--ds-target,1.75rem)] shrink-0 items-center justify-center rounded-300 text-base-subtle transition-colors hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down) hover:text-base-content"
          onClick={() => props.onToggleModules?.()}
        >
          <LayoutGrid size={20} aria-hidden="true" />
        </button>
      </Show>

      {/* THE BRAND, IN THE HEADER (2026-10-02, the owner: "theirs is on the
          left of the logo, I need the exact same layout"). Adobe's bar reads
          [grid][wordmark][menus] across the FULL width, with the rail and the
          section list below it — so the toggle can sit left of the mark. Ours
          had the mark in the sidebar, which put the glyph to its right and
          made the same order impossible. The mark moved here and the header
          now spans the whole width, exactly as theirs does. */}
      <Show when={props.appName}>
        <Link to="/" class="flex shrink-0 items-center gap-2 rounded-300 px-1 py-0.5 hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover)">
          <BrandMark size={22} class="shrink-0" color={props.brandColor} />
          <span class="hidden min-w-0 truncate text-sm font-bold tracking-tight sm:block">{props.appName}</span>
        </Link>
      </Show>

      {/* THE TRAIL IS GONE (2026-10-02, the owner: "we don't care about it").
          It was a Home glyph plus a chevron-separated path, and the two-level
          sidebar now says the same thing in the place you navigate from: the
          rail marks the area, the panel marks the screen inside it. Adobe's own
          header carries no breadcrumb either — brand, menus, controls. The
          spacer keeps the controls at the inline end. */}
      <div class="min-w-0 flex-1" />

      {/* Right: actions + icon buttons on the hit-area floor (was the
          source's 7×7 chrome; see the size-(--ds-target) note above). */}
      <div class="flex shrink-0 items-center gap-2">
        {props.actions}
        <Show when={props.user}>
          <button
            type="button"
            class="flex size-[var(--ds-target,1.75rem)] items-center justify-center rounded-300 text-base-subtle transition-colors hover:bg-(--ui-state-hover) focus-visible:bg-(--ui-state-hover) active:bg-(--ui-state-down) hover:text-base-content"
            aria-label="Jump to a page"
            aria-keyshortcuts="Meta+K Control+K"
            onClick={openCommandPalette}
          >
            <Search size={20} aria-hidden="true" />
          </button>
        </Show>
        {/* The help gate's ONLY affordance — Field hints render behind
            helpMode(), so without this control the gate is dead code. */}
        <HelpToggle />
        <ThemeToggle light={props.themes?.light} dark={props.themes?.dark} />
      </div>
    </header>
  );
}
